import { getFullVelocityUrl } from '@/Helper';
import { useHelp } from '@/hooks/useHelp';
import { HelpFeature, HelpModel } from '@/model/HelpModel';
import { QuestionMarkDarkIcon, QuestionMarkIcon, QuestionMarkLightIcon } from '@/resources/Icons';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import cx from 'classnames';
import React from 'react';
import styles from './HelpFactoryComponent.module.scss';

const fullVelocityUrl = getFullVelocityUrl();
const showPopup = (helpModel: HelpModel) => {
	const urlPath = `${fullVelocityUrl}${helpModel.Url}`;
	window.open(urlPath, '_blank', 'noopener,noreferrer');
};

type SizeType = 'small' | 'medium' | 'large';

type Props = {
	id?: string;
	disabled?: boolean;
	className?: string;
	helpFeature: HelpFeature;
	topicId?: string;
	isLight?: boolean;
	size?: SizeType;
};

type HelpComponentProps = {
	id?: string;
	disabled?: boolean;
	className?: string;
	helpTopics: HelpModel[];
	isLight?: boolean;
	size?: SizeType;
};

const HelpButton: React.FC<HelpComponentProps> = ({ id, disabled, className, helpTopics, isLight = false, size }) => {
	if (!helpTopics.length) {
		return <></>;
	}

	if (helpTopics.length > 1) {
		return <HelpDropdown id={id} disabled={disabled} className={className} helpTopics={helpTopics} />;
	}

	const item = helpTopics[0];
	const buttonColor: string = isLight ? styles.light : styles.dark;

	return (
		<Button
			id={id}
			disabled={disabled}
			className={cx(styles.helpButton, className, buttonColor, {
				[styles[size]]: size,
			})}
			onClick={() => {
				showPopup(item);
			}}
			title={`Help: ${item.Name}`}
			icon={isLight ? <QuestionMarkLightIcon className={styles.helpIcon} /> : <QuestionMarkDarkIcon className={styles.helpIcon} />}
			shape='circle'
		/>
	);
};

const HelpDropdown: React.FC<HelpComponentProps> = ({ id, disabled, className, helpTopics }) => {
	const menuItems: MenuProps['items'] = [];
	helpTopics.forEach(x => {
		menuItems.push({
			key: x.Id,
			label: x.Name,
			onClick: () => {
				showPopup(x);
			},
		});
	});

	const menuProps: MenuProps = {
		items: menuItems,
	};

	return (
		<Dropdown
			menu={menuProps}
			trigger={['hover']}
			disabled={disabled}
			mouseEnterDelay={0.1}
			mouseLeaveDelay={0.1}
			getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}>
			<Button id={id} disabled={disabled} className={cx(styles.helpButton, className)}>
				<Space align='center'>
					<QuestionMarkIcon className={styles.helpIcon} />
					Help
				</Space>
			</Button>
		</Dropdown>
	);
};

const HelpFactoryComponent: React.FC<Props> = ({ id, disabled, className, helpFeature, topicId, isLight = false, size }) => {
	//#region state management variables
	const helpTopics = useHelp(helpFeature, topicId);
	//#endregion

	if (helpTopics?.length === 0) {
		return <></>;
	}

	const topicIdIsEmpty = !topicId?.length;
	if (topicIdIsEmpty) {
		return <HelpDropdown id={id} disabled={disabled} className={className} helpTopics={helpTopics} />;
	}

	return <HelpButton id={id} disabled={disabled} className={className} helpTopics={helpTopics} isLight={isLight} size={size} />;
};

export { HelpFactoryComponent };
